import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Container from '../../../Core/Container';
import Link from '../../../Core/Link';
import Image from '../../../Core/Image';
import MultiColumnLayout from '../../Shared/MultiColumnLayout';

export const Wrapper = styled.div`
  background-color: ${({ colourOverrideBackground }) =>
    colourOverrideBackground || themeGet('colors.accent')};
  background-image: url(${({ background }) => background});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  &.proven-results-section {
    background-color: ${({ colourOverrideBackground }) =>
      colourOverrideBackground || themeGet('colors.neutral_2')};
  }
`;

export const StyledContainer = styled(Container)`
  padding: 50px 0 60px;

  display: flex;
  flex-direction: column;

  &.with-image {
    padding-bottom: 0;
  }

  > h1,
  > h2,
  > h3,
  > h4 {
    color: ${themeGet('colors.text_primary')};
    text-align: center;

    margin-bottom: 30px;
  }

  ${Wrapper}.text-dark & {
    > h1,
    > h2,
    > h3,
    > h4 {
      color: ${themeGet('colors.text-secondary')};
    }
  }

  ${Wrapper}.proven-results-section & {
    > h1,
    > h2,
    > h3,
    > h4 {
      margin-bottom: 35px;
    }
    h1 {
      font-size: 26px;
      line-height: 34px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 50px 45px 60px;

    ${Wrapper}.data-section & {
      padding: 50px 90px 60px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 70px 0px 90px;

    ${Wrapper}.data-section & {
      padding: 70px 0px 90px;
    }

    ${Wrapper}.proven-results-section & {
      padding: 70px 0px 90px;

      > h1,
      > h2,
      > h3,
      > h4 {
        margin-bottom: 60px;
      }

      h1 {
        font-size: 36px;
        line-height: 46px;
      }
    }

    > h1,
    > h2,
    > h3,
    > h4 {
      margin-bottom: 30px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 70px 0px 90px;
    ${Wrapper}.proven-results-section & {
      padding: 70px 45px 90px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.max')}) {
    padding: 70px 0px 90px;
    ${Wrapper}.proven-results-section & {
      padding: 70px 90px 90px;
    }
  }
`;

export const StyledLink = styled(Link)`
  margin-top: 40px;
  align-self: center;
`;

export const Columns = styled(MultiColumnLayout)``;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 40px;

  padding-top: 12px;

  ${Wrapper}.data-section & {
    padding-top: 0;
    padding-bottom: 20px;
  }

  ${Wrapper}.proven-results-section & {
    border-bottom: none;
    padding-bottom: 0;

    padding-top: 0;
  }

  &:nth-child(-n + 1) {
    padding-top: 0;
  }

  &::before {
    content: '${({ text }) => text}';
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: ${themeGet('colors.orange')};
    font-family: ${themeGet('fonts.secondary')};
    font-size: 46px;
    letter-spacing: 0;
    line-height: 40px;
    margin-bottom: 15px;

    ${Wrapper}.proven-results-section & {
      min-height: auto;
      min-width: auto;
      border-radius: 0;
      background-color: transparent;
      color: ${({ focusTextColour }) =>
        focusTextColour || themeGet('colors.primary')};
      font-size: 72px;
      letter-spacing: 0;
      line-height: 84px;
      margin-bottom: 0;
    }
  }

  p {
    margin-bottom: 45px;
  }

  ${Wrapper}.proven-results-section & {
    p {
      margin-bottom: 0;
    }
  }

  @media only screen and (max-width: ${themeGet('breakpoints.lg')}) {
    /* for IE */
    width: 100%;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;
    align-items: center;

    ${Wrapper}.data-section & {
      flex-direction: column;
      align-items: flex-start;

      &::before {
        font-size: 86px;
        letter-spacing: 0;
        line-height: 80px;
        margin-bottom: 15px;
      }
    }

    &::before {
      margin-right: 30px;
      margin-bottom: 0;

      ${Wrapper}.proven-results-section & {
        min-height: auto;
        min-width: auto;
        margin-bottom: 0;
        min-width: 135px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: column;
    align-items: flex-start;
    padding-top: 0;

    ${Wrapper}.data-section & {
      padding-left: 15px;
      padding-right: 15px;
      padding-bottom: 40px;
    }

    &:nth-child(-n + 3) {
      padding-top: 0;
    }

    &::before {
      margin-bottom: 15px;

      font-size: 86px;
      letter-spacing: 0;
      line-height: 80px;

      ${Wrapper}.proven-results-section & {
        min-height: auto;
        min-width: auto;
        margin-bottom: 25px;
        font-size: 86px;
        letter-spacing: 0;
        line-height: 80px;
        flex-direction: column;
      }
    }
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  /* for IE */
  width: 100%;

  ${Wrapper}.proven-results-section & {
    /* for IE */
    width: 100%;
  }

  a {
    margin-top: auto;
    display: inline-block;
    padding-bottom: 1px;
  }

  h3,
  p {
    /* for IE */
    width: 100%;
  }

  > h1:first-child,
  > h2:first-child,
  > h3:first-child,
  > h4:first-child,
  > h5:first-child,
  > h6:first-child {
    a {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      color: ${themeGet('colors.darkest-orange')};
      text-decoration: underline;

      &:hover,
      &:focus {
        color: ${themeGet('colors.orange')};
      }
    }
    color: ${themeGet('colors.text_primary')};
    text-align: left;
    margin-bottom: 20px;

    ${Wrapper}.text-dark & {
      color: ${themeGet('colors.text-secondary')};
    }

    ${Wrapper}.proven-results-section & {
      margin-bottom: 10px;
    }
  }
  > h1:nth-child(2),
  > h2:nth-child(2),
  > h3:nth-child(2),
  > h4:nth-child(2),
  > h5:nth-child(2),
  > h6:nth-child(2) {
    margin-top: auto;
    a {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      color: ${themeGet('colors.darkest-orange')};
      text-decoration: underline;
      &:hover,
      &:focus {
        color: ${themeGet('colors.orange')};
      }
    }
  }
  > p a {
    font-size: 16px;
  }

  @media only screen and (max-width: ${themeGet('breakpoints.lg')}) {
    /* for IE */
    width: 100%;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    ${Wrapper}.proven-results-section & {
      > h1:first-child,
      > h2:first-child,
      > h3:first-child,
      > h4:first-child {
        margin-bottom: 10px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    ${Wrapper}.proven-results-section & {
      > h1:first-child,
      > h2:first-child,
      > h3:first-child,
      > h4:first-child {
        margin-bottom: 30px;
      }
    }
    > p a {
      font-size: 18px;
    }
  }
`;

export const OptionalImage = styled(Image)`
  width: auto;
  height: 100%;
  max-height: 150px;
  margin: 30px auto 0;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
  }
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-height: 100%;
    width: 100%;
    max-width: 572px;
  }
`;
